import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";
import { mediaList } from "@/mocks/media";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const count = heroSlides.length;

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, count]);

  const go = (next: number) => setIndex((next + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  const current = heroSlides[index];
  const media = mediaList.find((m) => m.id === current.mediaId);

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} 影视推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-medium mb-4">
              <i className="ri-flashlight-fill"></i>
              {current.kicker}
            </span>
            <h1 className="font-heading font-bold text-white text-3xl md:text-5xl leading-tight tracking-tight">
              {current.title}
            </h1>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-white/80 max-w-xl leading-relaxed">
              {current.subtitle}
            </p>

            {media && (
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-white/70">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  <strong className="text-white font-semibold">{media.rating.toFixed(1)}</strong>
                </span>
                <span>{media.year}</span>
                <span>{media.region}</span>
                <span>{media.duration}</span>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.mediaId}`}
                className="h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium flex items-center gap-2 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to={media?.type === "tv" ? "/category/tv" : "/category/movie"}
                className="h-11 px-6 rounded-md bg-white/10 backdrop-blur border border-white/30 text-white text-sm font-medium flex items-center gap-2 hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览更多
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white items-center justify-center hover:bg-white/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white items-center justify-center hover:bg-white/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* dots */}
      <div className="absolute bottom-6 right-6 md:right-10 z-20 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}